import { ChangeDetectorRef, Component, ElementRef, OnInit, OnDestroy } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { CustomValidators } from 'app/main/helpers/custom-validators';
import { combineLatest, forkJoin, Observable, of, startWith, Subject, takeUntil } from 'rxjs';
import { NaceCode } from 'app/@types/shared/nace-code';
import { OnboardingDto } from 'app/@types/dto/onboarding-dto.model';
import { AdminHTTPService } from '@services/datasources/admin-http-service';
import { ActivatedRoute } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { AppRoutingService } from '@services/app-routing/app-routing.service';
import { AdminDataSyncService } from '@services/users/admin-data-sync.service';
import { Store } from '@ngxs/store';
import { buildCountryCitiesLUT, City } from 'app/@types/shared/world-cities';
import { ConfigService } from 'app/app-config.service';
import { AuthenticationService } from 'app/auth/service';
import { Country as CountryType } from 'app/@types/shared/world-cities';
import naceData from '@assets/data/nace.json';
import countryData from '@assets/data/countries.json';
import { ClientAppConfig } from 'app/@types/shared/app-config';
import { UtilsService } from '@services/common/utils.service';
import { ToastService } from '@services/common/toast.service';
import { PricingPlanDto } from 'app/@types/dto/pricing-plan-dto.model';
import { FeatureService } from '@services/features/feature.service';
import { OrganizationPricingPlanDto } from 'app/@types/dto/organization-pricing-plan-dto.model';
import { SelectChangeEvent } from 'primeng/select';
import { buildCountryOptions, CountryOption } from 'app/main/helpers/country-options';
import { revealFormErrors } from 'app/main/helpers/form-validation';
import { destructiveConfirmation } from 'app/main/helpers/confirmation';

enum PricingPlanType {
    FREE = 'bc1b9506-260b-4b3c-b680-126505a307cb',
    STARTER = 'fdb67fda-da8e-431f-b2b6-868c7000c336',
    SCAN = '3c599bd7-4536-4432-b439-026bd2edd787',
}

interface OrganizationFormGroupControls {
    companyLogo: FormControl<string | null>;
    companyName: FormControl<string | null>;
    vatNumber: FormControl<string | null>;
    country: FormControl<string | null>;
    city: FormControl<number | string | null>;
    naceCode: FormControl<string | null>;
    industryCode: FormControl<string | null>;
    fte: FormControl<number | null>;
}

interface UserFormGroupControls {
    lastname: FormControl<string | null>;
    firstname: FormControl<string | null>;
    role: FormControl<string | null>;
    email: FormControl<string | null>;
    phoneNumber: FormControl<string | null>;
    defaultLocale: FormControl<string | null>;
}

interface LicenseFormGroupControls {
    license: FormControl<string | null>;
    users: FormControl<number | null>;
    entities: FormControl<number | null>;
    period: FormControl<boolean | null>;
    validity: FormControl<Date | null>;
    expert: FormControl<boolean | null>;
    solutions: FormControl<boolean | null>;
    csrd: FormControl<boolean | null>;
    reduce: FormControl<boolean | null>;
    mcpAccess: FormControl<boolean | null>;
}

interface OrganizationCreationFormControls {
    organizationGroup: FormGroup<OrganizationFormGroupControls>;
    userGroup: FormGroup<UserFormGroupControls>;
    licenseGroup: FormGroup<LicenseFormGroupControls>;
}

@Component({
    selector: 'app-organization-creation-form',
    templateUrl: './organization-creation-form.component.html',
    styleUrls: ['./organization-creation-form.component.scss'],
    providers: [ConfirmationService],
    standalone: false,
})
export class OrganizationCreationFormComponent implements OnInit, OnDestroy {
    public settingsForm!: FormGroup<OrganizationCreationFormControls>;
    public isMainAdmin: boolean = false;
    public countries: CountryOption[] = [];
    private countryCitiesLUT: Map<string, City[]> = new Map<string, City[]>();
    public filteredCities: City[] | undefined;
    public client: ClientAppConfig;
    public clientName: string = '';
    public availableLanguages: string[] = [];
    public isLoading = false;
    public availablePlans: PricingPlanDto[] = [];
    private defaultPlan?: PricingPlanDto =
        this.configService.getClient.name.toLowerCase() === 'dcarbonize'
            ? this.availablePlans.find((p) => p.uid === PricingPlanType.FREE)
            : this.availablePlans.find((p) => p.uid === PricingPlanType.STARTER);
    public selectedPlan?: PricingPlanDto;
    public minDate: Date = new Date();

    get organizationFormGroup(): FormGroup<OrganizationFormGroupControls> {
        return this.settingsForm.controls.organizationGroup;
    }

    get userFormGroup(): FormGroup<UserFormGroupControls> {
        return this.settingsForm.controls.userGroup;
    }

    get licenseFormGroup(): FormGroup<LicenseFormGroupControls> {
        return this.settingsForm.controls.licenseGroup;
    }

    // The owner block only makes sense with exactly one Main user: it stays visible while
    // creating (the owner is being entered) and on single-owner organizations (read-only).
    // With zero or several owners the backend leaves the user fields empty, so the section
    // is hidden and owners are managed through the user list instead.
    get showOwnerSection(): boolean {
        return !this.onboardingInfo?.name || (this.onboardingInfo.mainUserCount ?? 1) === 1;
    }

    public activities: NaceCode[] = [];
    public onboardingInfo?: OnboardingDto;
    public selectedIndustryActivity?: NaceCode;
    private ngUnsubscribe = new Subject<void>();
    public vatPlaceholder = 'BE0123.456.789';
    public config = this.configService.getConfig;

    constructor(
        private configService: ConfigService,
        private authenticationService: AuthenticationService,
        private translateService: CustomTranslateService,
        private featureService: FeatureService,
        private adminHttpCalls: AdminHTTPService,
        private route: ActivatedRoute,
        private confirmationService: ConfirmationService,
        private appRouting: AppRoutingService,
        private adminSignals: AdminDataSyncService,
        private cdr: ChangeDetectorRef,
        private store: Store,
        private readonly utilsService: UtilsService,
        private toastService: ToastService,
        private readonly host: ElementRef<HTMLElement>,
    ) {
        this.client = this.configService.getClient;

        this.initFormControls();
    }

    private initFormControls(): void {
        this.settingsForm = new FormGroup<OrganizationCreationFormControls>({
            organizationGroup: new FormGroup<OrganizationFormGroupControls>({
                companyLogo: new FormControl<string | null>(null),
                companyName: new FormControl<string | null>(null, Validators.required),
                // Validated live against the selected country's format (see countryVatValidator);
                // an untouched legacy VAT loaded for editing is tolerated and never blocks saving.
                vatNumber: new FormControl<string | null>(null, [CustomValidators.countryVatValidator]),
                country: new FormControl<string | null>('', Validators.required),
                city: new FormControl<number | string | null>(''),
                naceCode: new FormControl<string | null>(null),
                industryCode: new FormControl<string | null>({ value: null, disabled: true }),
                fte: new FormControl<number | null>(0),
            }),
            userGroup: new FormGroup<UserFormGroupControls>({
                lastname: new FormControl<string | null>(null, Validators.required),
                firstname: new FormControl<string | null>(null, Validators.required),
                role: new FormControl<string | null>(null),
                email: new FormControl<string | null>(null, [Validators.required, CustomValidators.emailValidator]),
                phoneNumber: new FormControl<string | null>(null, [CustomValidators.phoneNumberValidator]),
                defaultLocale: new FormControl<string | null>(this.configService.getConfig.defaultLocale),
            }),
            licenseGroup: new FormGroup<LicenseFormGroupControls>({
                license: new FormControl<string | null>(null, Validators.required),
                users: new FormControl<number | null>(1, [Validators.required, Validators.min(1)]),
                entities: new FormControl<number | null>(1, [Validators.required, Validators.min(1)]),
                period: new FormControl<boolean | null>(false),
                validity: new FormControl<Date | null>(new Date(), Validators.required),
                expert: new FormControl<boolean | null>(false),
                solutions: new FormControl<boolean | null>(false),
                csrd: new FormControl<boolean | null>(false),
                reduce: new FormControl<boolean | null>(false),
                mcpAccess: new FormControl<boolean | null>(false),
            }),
        });

        this.initializeFormValueSubscriptions();
    }

    ngOnInit(): void {
        this.isLoading = true;
        this.clientName = this.client.name;
        this.availableLanguages = this.configService.getConfig.supportedLanguages;
        this.isMainAdmin = Boolean(this.route.snapshot.data['isMainAdmin']);

        // Combine all initialization observables
        combineLatest([this.adminHttpCalls.getPricingPlans(), this.translateService.onLangChange.pipe(startWith(null))])
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(([plans, _]) => {
                // Initialize activities
                this.activities = naceData.map((item) => {
                    return {
                        ...item,
                        label: this.translateService.instant(`nace.${item.translationKey}`),
                        industrySectorLabel: this.translateService.instant(
                            `components.settingsForm.industry.${item.industrySectorLabel}`,
                        ),
                    };
                });

                // Initialize pricing plans
                this.availablePlans = plans.sort((a, b) => {
                    // Mettre FREE ou STARTER en premier
                    if (
                        (a.uid === PricingPlanType.FREE || a.uid === PricingPlanType.STARTER) &&
                        !(b.uid === PricingPlanType.FREE || b.uid === PricingPlanType.STARTER)
                    ) {
                        return -1;
                    }
                    if (
                        !(a.uid === PricingPlanType.FREE || a.uid === PricingPlanType.STARTER) &&
                        (b.uid === PricingPlanType.FREE || b.uid === PricingPlanType.STARTER)
                    ) {
                        return 1;
                    }
                    // Mettre SCAN après
                    if (a.uid === PricingPlanType.SCAN && b.uid !== PricingPlanType.SCAN) {
                        return 1;
                    }
                    if (a.uid !== PricingPlanType.SCAN && b.uid === PricingPlanType.SCAN) {
                        return -1;
                    }
                    return 0;
                });
                this.defaultPlan =
                    this.configService.getClient.name.toLowerCase() === 'dcarbonize'
                        ? plans.find((p) => p.uid === PricingPlanType.FREE)
                        : plans.find((p) => p.uid === PricingPlanType.STARTER);

                // Set default license after plans are loaded
                const licenseControl = this.licenseFormGroup.controls.license;
                if (!licenseControl.value) {
                    licenseControl.setValue(this.defaultPlan?.uid ?? null);
                }

                // Initialize countries
                this.translateService.get('countries').subscribe((countries) => {
                    this.countries = buildCountryOptions(countries, this.translateService.currentLang);
                });

                // Initialize form values ithis.f needed
                this.adminSignals.currentSelectedOrgId.subscribe((organizationId) => {
                    this.countryCitiesLUT = buildCountryCitiesLUT();
                    this.settingsForm.reset();
                    if (organizationId) {
                        this.settingsForm.disable();
                        this.adminHttpCalls
                            .getOnboardingInfo(organizationId)
                            .pipe(takeUntil(this.ngUnsubscribe))
                            .subscribe({
                                next: (onboardingDto) => {
                                    this.onboardingInfo = onboardingDto;
                                    this.adminSignals.updateTitleSubject.next(this.onboardingInfo.name);
                                    this.settingsForm.enable();
                                    this.initFormValues();
                                    this.isLoading = false;
                                },
                                error: () => {
                                    this.settingsForm.enable();
                                    //TODO:handleError
                                    this.isLoading = false;
                                },
                            });
                    } else {
                        this.adminSignals.updateTitleSubject.next('new_organization');
                        this.settingsForm.enable();
                        this.organizationFormGroup.get('city')?.disable();
                        this.onboardingInfo = undefined;
                        this.initFormValues();
                        this.isLoading = false;
                    }
                });
            });
    }

    private initializeFormValueSubscriptions(): void {
        this.organizationFormGroup.controls.naceCode.valueChanges.subscribe((selectedNace) => {
            [this.selectedIndustryActivity] = this.activities.filter((code) => code.id === selectedNace);
            if (this.selectedIndustryActivity) {
                this.organizationFormGroup.controls.industryCode.setValue(
                    this.selectedIndustryActivity.industrySectorId,
                );
            } else {
                this.organizationFormGroup.controls.industryCode.reset();
            }
        });

        this.userFormGroup.controls.email.valueChanges.subscribe((value) => {
            if (value) {
                const lowerCaseValue = value.toLowerCase();
                if (value !== lowerCaseValue) {
                    this.userFormGroup.controls.email.setValue(lowerCaseValue, { emitEvent: false });
                }
            }
        });

        this.licenseFormGroup.controls.license.valueChanges
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((licenseId) => {
                if (licenseId) {
                    this.handleLicenseTypeChange(licenseId);
                }
            });
    }

    onCountryChange(event: SelectChangeEvent): void {
        const countryCode = typeof event.value === 'string' ? event.value : null;
        const country = countryData.find((c: CountryType) => c.id === countryCode);
        const vatNumberControl = this.organizationFormGroup.get('vatNumber');

        this.organizationFormGroup.get('country')?.setValue(countryCode);

        // Keep the entered VAT and re-check it against the new country: mark it touched so a number
        // that is no longer valid surfaces its error (red outline + message) immediately.
        vatNumberControl?.markAsTouched();
        vatNumberControl?.updateValueAndValidity();
        this.vatPlaceholder = country?.placeholderVAT ?? 'BE0123.456.789';
    }

    // Validate as soon as the user leaves the field (clicks away), not only after editing it.
    onVatBlur(): void {
        const vatNumberControl = this.organizationFormGroup.get('vatNumber');
        vatNumberControl?.markAsTouched();
        vatNumberControl?.updateValueAndValidity();
    }

    private handleLicenseTypeChange(id: string): void {
        const plan = this.availablePlans.find((p) => p.uid === id);
        if (!plan) {
            return;
        }

        this.selectedPlan = plan;
        const { users, entities, period, validity, expert, solutions, csrd, reduce, mcpAccess } =
            this.licenseFormGroup.controls;

        if (plan.uid === PricingPlanType.FREE || plan.uid === PricingPlanType.STARTER) {
            users.setValue(plan.doc.max_users);
            entities.setValue(plan.doc.max_entities);
            period.setValue(plan.doc.multi_period);
            validity.setValue(
                new Date(
                    this.onboardingInfo?.licenseValidity ??
                        Date.now() + plan.doc.default_validity * 24 * 60 * 60 * 1000,
                ),
            );
            expert.setValue(plan.doc.expert);
            solutions.setValue(plan.doc.solutions);
            csrd.setValue(plan.doc.csrd);
            reduce.setValue(plan.doc.reduce);
            users.disable();
            entities.disable();
            expert.disable();
            solutions.disable();
            csrd.disable();
            reduce.disable();
        } else {
            users.setValue(
                this.onboardingInfo &&
                    this.onboardingInfo.licenseDoc &&
                    this.onboardingInfo.licenseDoc.max_users > plan.doc.max_users
                    ? (this.onboardingInfo.licenseDoc.max_users ?? plan.doc.max_users)
                    : plan.doc.max_users,
            );
            entities.setValue(
                this.onboardingInfo &&
                    this.onboardingInfo.licenseDoc &&
                    this.onboardingInfo.licenseDoc.max_entities > plan.doc.max_entities
                    ? (this.onboardingInfo.licenseDoc.max_entities ?? plan.doc.max_entities)
                    : plan.doc.max_entities,
            );
            period.setValue(plan.doc.multi_period);
            validity.setValue(
                new Date(
                    this.onboardingInfo?.licenseValidity ??
                        Date.now() + plan.doc.default_validity * 24 * 60 * 60 * 1000,
                ),
            );
            expert.setValue(this.onboardingInfo?.licenseDoc?.expert ?? plan.doc.expert);
            solutions.setValue(this.onboardingInfo?.licenseDoc?.solutions ?? plan.doc.solutions);
            csrd.setValue(this.onboardingInfo?.licenseDoc?.csrd ?? plan.doc.csrd);
            reduce.setValue(this.onboardingInfo?.licenseDoc?.reduce ?? plan.doc.reduce);

            users.enable();
            entities.enable();
            expert.enable();
            solutions.enable();
            csrd.enable();
            reduce.enable();
        }

        // MCP access is a per-organization grant, not a plan feature: no plan ever sets it, and
        // only the D-Carbonize platform admin may toggle it — global and dedicated admins see it
        // disabled (the backend rejects the change for them too).
        mcpAccess.setValue(this.onboardingInfo?.licenseDoc?.mcpAccess ?? false);
        if (this.userIsPlatformAdmin()) {
            mcpAccess.enable();
        } else {
            mcpAccess.disable();
        }
    }

    initFormValues(): void {
        const organizationGroup = this.organizationFormGroup;
        const userGroup = this.userFormGroup;
        this.isLoading = true;
        const licenseGroup = this.licenseFormGroup;
        const onboarding = this.onboardingInfo;

        organizationGroup.controls.companyLogo.setValue(onboarding?.avatar ?? null);
        organizationGroup.controls.companyName.setValue(onboarding?.name ?? null);
        organizationGroup.controls.vatNumber.setValue(onboarding?.tva ?? null);
        organizationGroup.controls.country.setValue(onboarding?.country ?? null);
        // Show the loaded country's VAT format as the hint. The value stays pristine, so a
        // legacy-invalid VAT is displayed but not flagged until the admin actually edits it.
        const loadedCountry = countryData.find((c: CountryType) => c.id === onboarding?.country);
        this.vatPlaceholder = loadedCountry?.placeholderVAT ?? 'BE0123.456.789';
        this.filteredCities = this.countryCitiesLUT.get(onboarding?.country ?? '');
        organizationGroup.controls.city.setValue(onboarding?.cityCode ?? null);
        organizationGroup.controls.naceCode.setValue(onboarding?.nace ?? null);
        organizationGroup.controls.industryCode.setValue(onboarding?.industryId ?? null);

        userGroup.controls.lastname.setValue(onboarding?.lastName ?? null);
        userGroup.controls.firstname.setValue(onboarding?.firstName ?? null);
        userGroup.controls.role.setValue(onboarding?.role ?? null);
        userGroup.controls.email.setValue(onboarding?.email ?? null);
        const rawPhone = onboarding?.phone_number;
        const phoneNumber: string | null = rawPhone !== undefined && rawPhone !== '+3200000000000' ? rawPhone : null;
        userGroup.controls.phoneNumber.setValue(phoneNumber);
        userGroup.controls.defaultLocale.setValue(onboarding?.locale ?? this.configService.getConfig.defaultLocale);

        licenseGroup.controls.license.setValue(onboarding?.licenseId ?? this.defaultPlan?.uid ?? null);
        licenseGroup.controls.users.setValue(
            onboarding?.licenseDoc?.max_users ?? this.defaultPlan?.doc.max_users ?? null,
        );
        licenseGroup.controls.entities.setValue(
            onboarding?.licenseDoc?.max_entities ?? this.defaultPlan?.doc.max_entities ?? null,
        );
        licenseGroup.controls.period.setValue(
            onboarding?.licenseDoc?.multi_period ?? this.defaultPlan?.doc.multi_period ?? null,
        );
        licenseGroup.controls.validity.setValue(
            onboarding?.licenseValidity
                ? new Date(onboarding.licenseValidity)
                : new Date(Date.now() + (this.defaultPlan?.doc.default_validity ?? 0) * 24 * 60 * 60 * 1000),
        );
        licenseGroup.controls.expert.setValue(onboarding?.licenseDoc?.expert ?? this.defaultPlan?.doc.expert ?? null);
        licenseGroup.controls.solutions.setValue(
            onboarding?.licenseDoc?.solutions ?? this.defaultPlan?.doc.solutions ?? null,
        );
        licenseGroup.controls.csrd.setValue(onboarding?.licenseDoc?.csrd ?? this.defaultPlan?.doc.csrd ?? null);
        licenseGroup.controls.reduce.setValue(onboarding?.licenseDoc?.reduce ?? this.defaultPlan?.doc.reduce ?? null);
        licenseGroup.controls.mcpAccess.setValue(onboarding?.licenseDoc?.mcpAccess ?? false);

        organizationGroup.markAsPristine();
        userGroup.markAsPristine();
        licenseGroup.markAsPristine();
        if (onboarding?.name) {
            userGroup.disable();
        } else {
            userGroup.enable();
        }
        this.isLoading = false;
        if (this.userIsGlobalAdmin()) {
            licenseGroup.enable();
        } else {
            licenseGroup.disable();
        }
        // The group-level enable() above re-enables every control: re-assert that MCP access
        // stays platform-admin only.
        if (this.userIsPlatformAdmin()) {
            licenseGroup.controls.mcpAccess.enable();
        } else {
            licenseGroup.controls.mcpAccess.disable();
        }
    }

    filterCities() {
        const country = this.organizationFormGroup.controls.country.value;
        if (country) {
            this.filteredCities = this.countryCitiesLUT.get(country);
            this.organizationFormGroup.controls.city.enable();
        }
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    /**
     * The parts of the form a submit checks. The owner fields count only while they can be edited;
     * the license counts only for an admin who can change it, and only its type and end date unless
     * it is a Scan plan (the backend fills the rest of a Free or Starter license).
     */
    private controlsToCheck(): AbstractControl[] {
        const license = this.licenseFormGroup;
        const checked: AbstractControl[] = [this.organizationFormGroup];
        if (!this.userFormGroup.disabled) {
            checked.push(this.userFormGroup);
        }
        if (!license.disabled) {
            checked.push(
                ...(license.controls.license.value === PricingPlanType.SCAN
                    ? [license]
                    : [license.controls.license, license.controls.validity]),
            );
        }
        return checked;
    }

    onSubmit() {
        // Reveal the errors of every checked part, not only those of the first wrong one.
        const checks = this.controlsToCheck().map((control) => revealFormErrors(control, this.host.nativeElement));
        if (checks.includes(false)) {
            return;
        }
        this.isLoading = true;
        const orgControls = this.organizationFormGroup.controls;
        const userControls = this.userFormGroup.controls;
        const licenseControls = this.licenseFormGroup.controls;
        const onboardingInfo = this.onboardingInfo;

        const validityValue = licenseControls.validity.value;
        const licenseValidity = validityValue ? this.toNeutralDate(validityValue) : undefined;

        // Extract primitive values once to get type-safe access and apply DTO-compatible defaults.
        const companyLogo = orgControls.companyLogo.value ?? undefined;
        const companyName = orgControls.companyName.value ?? '';
        const vatNumber = orgControls.vatNumber.value ?? undefined;
        const country = orgControls.country.value ?? undefined;
        const cityCodeRaw = orgControls.city.value;
        const nace = orgControls.naceCode.value ?? undefined;
        const industryId = orgControls.industryCode.value ?? undefined;
        const firstName = userControls.firstname.value ?? undefined;
        const lastName = userControls.lastname.value ?? undefined;
        const email = userControls.email.value ?? '';
        const phone_number = userControls.phoneNumber.value?.replace(/[^0-9+]/g, '');
        const locale = userControls.defaultLocale.value ?? undefined;
        const role = userControls.role.value ?? undefined;
        const licenseId = licenseControls.license.value ?? undefined;
        const max_entities = licenseControls.entities.value ?? 0;
        const max_users = licenseControls.users.value ?? 0;
        const multi_period = licenseControls.period.value ?? undefined;
        const expert = licenseControls.expert.value ?? false;
        const solutions = licenseControls.solutions.value ?? false;
        const csrd = licenseControls.csrd.value ?? false;
        const reduce = licenseControls.reduce.value ?? false;
        const mcpAccess = licenseControls.mcpAccess.value ?? false;

        if (!onboardingInfo?.name) {
            // OnboardingDto.cityCode is a numeric id from the dropdown (optional).
            const cityCode = typeof cityCodeRaw === 'number' ? cityCodeRaw : undefined;
            this.adminHttpCalls
                .createOrganizationUser(
                    {
                        avatar: companyLogo,
                        name: companyName,
                        tva: vatNumber,
                        country,
                        cityCode,
                        nace,
                        industryId,
                        firstName,
                        lastName,
                        email,
                        phone_number,
                        locale,
                        role,
                        licenseId,
                        licenseDoc: {
                            max_entities,
                            max_users,
                            multi_period,
                            expert,
                            solutions,
                            csrd,
                            reduce,
                            mcpAccess,
                        },
                        licenseValidity,
                    },
                    this.clientName,
                    // this.isMainAdmin ? this.adminSignals.currentSelectedDedicatedAdminId.getValue() : undefined,
                    this.adminSignals.currentSelectedDedicatedAdminId.getValue(),
                )
                .subscribe({
                    next: (result) => {
                        this.onboardingInfo = result;
                        this.adminSignals.currentSelectedOrgId.next(result.uid ?? undefined);
                        this.adminSignals.udpateListSubject.next(true);

                        this.appRouting.navigateToOganisationForm();
                        this.adminSignals.updateTitleSubject.next(result.name);
                        this.settingsForm.markAsPristine();
                        this.toastService.showSuccess({
                            details: this.translateService.instant('admin.dedicatedAdmin.organization_created'),
                        });
                        this.isLoading = false;
                    },
                    error: () => {
                        this.isLoading = false;
                        this.toastService.showError({
                            details: this.translateService.instant('admin.dedicatedAdmin.organization_created_fail'),
                        });
                    },
                });
        } else {
            const uid = onboardingInfo.uid;
            if (!uid) {
                // An update always has the uid of the organization it edits; defensive guard.
                this.handleOrganizationUpdateFail();
                return;
            }
            // OrganizationDto.cityCode is a required string; coerce whatever the dropdown produced.
            const cityCode = cityCodeRaw !== null && cityCodeRaw !== undefined ? String(cityCodeRaw) : '';
            const organizationUpdate$ = this.adminHttpCalls.updateOrganization(uid, {
                uid,
                avatar: companyLogo,
                name: companyName,
                tva: vatNumber,
                country,
                cityCode,
                nace,
                industryId,
                isActive: true,
                isOnboarded: onboardingInfo.isOnboarded ?? false,
            });
            let pricingPlanUpdate$ = of(null) as Observable<OrganizationPricingPlanDto | null>;
            if (this.userIsGlobalAdmin()) {
                if (!licenseValidity) {
                    // Global admin path requires a validity date; defensive guard since
                    // the submit checks it when the license group is enabled.
                    this.handleOrganizationUpdateFail();
                    return;
                }
                pricingPlanUpdate$ = this.adminHttpCalls.updateOrganizationPricingPlan(uid, {
                    organizationId: uid,
                    pricingPlanId: licenseId ?? '',
                    licenseDoc: { max_entities, max_users, multi_period, expert, solutions, csrd, reduce, mcpAccess },
                    validUntil: licenseValidity,
                });
            }

            // Execute both updates in parallel
            forkJoin([organizationUpdate$, pricingPlanUpdate$]).subscribe({
                next: ([result]) => {
                    if (!this.onboardingInfo) {
                        return;
                    }
                    this.onboardingInfo.name = result.name;
                    this.onboardingInfo.tva = result.tva;
                    this.onboardingInfo.nace = result.nace;
                    this.onboardingInfo.industryId = result.industryId;
                    this.onboardingInfo.requestedSolutions = result.requestedSolutions;

                    this.adminSignals.udpateListSubject.next(true);
                    this.adminSignals.updateTitleSubject.next(this.onboardingInfo.name);
                    this.settingsForm.markAsPristine();
                    this.isLoading = false;
                    this.toastService.showSuccess({
                        details: this.translateService.instant('admin.dedicatedAdmin.organization_edited'),
                    });
                },
                error: () => this.handleOrganizationUpdateFail(),
            });
        }
    }

    delete(): void {
        const onboardingInfo = this.onboardingInfo;
        if (!onboardingInfo) {
            return;
        }
        combineLatest([
            this.translateService.get('components.settingsForm.confirmDeletion', {
                name: onboardingInfo.name,
            }),
            this.translateService.get('common.confirm_title'),
            this.translateService.get('common.confirm_button'),
            this.translateService.get('common.cancel_button'),
        ]).subscribe((translations) => {
            this.confirmationService.confirm(
                destructiveConfirmation({
                    message: this.utilsService.handleTranslation(translations[0]),
                    header: this.utilsService.handleTranslation(translations[1]),
                    acceptLabel: this.utilsService.handleTranslation(translations[2]),
                    rejectLabel: this.utilsService.handleTranslation(translations[3]),
                    icon: 'ms ms-warning',
                    accept: () => {
                        const uid = this.onboardingInfo?.uid;
                        if (uid) {
                            this.adminHttpCalls
                                .deleteOrganization(uid)
                                .pipe(takeUntil(this.ngUnsubscribe))
                                .subscribe({
                                    next: () => {
                                        this.adminSignals.udpateListSubject.next(true);
                                        // this.isMainAdmin
                                        //     ? this.appRouting.navigateToOganisationAdminForm()
                                        //     : this.appRouting.navigateToOganisationForm();
                                        this.appRouting.navigateToOganisationAdminForm();
                                    },
                                    error: () => this.handleOrganizationUpdateFail(),
                                });
                        }
                    },
                    reject: () => {
                        // noop — user cancelled the confirmation dialog
                    },
                }),
            );
        });
    }

    handleOrganizationUpdateFail() {
        this.isLoading = false;
        this.toastService.showError({
            details: this.translateService.instant('admin.dedicatedAdmin.organization_edited_fail'),
        });
        this.adminSignals.currentSelectedOrgId.next(undefined);
        this.adminSignals.udpateListSubject.next(true);
        // this.isMainAdmin
        //     ? this.appRouting.navigateToOganisationAdminForm()
        //     : this.appRouting.navigateToOganisationForm();
        this.appRouting.navigateToOganisationAdminForm();
    }

    public userIsGlobalAdmin(): boolean {
        return (
            this.featureService.hasUiFeatures(['admin_admin'], false) ||
            this.featureService.hasUiFeatures(['admin_admin'], true)
        );
    }

    /** D-Carbonize platform admin — a tier above the tenant global admin (admin_admin). */
    public userIsPlatformAdmin(): boolean {
        return (
            this.featureService.hasUiFeatures(['dcarb_admin_admin'], false) ||
            this.featureService.hasUiFeatures(['dcarb_admin_admin'], true)
        );
    }

    isLicenseCustomizable(): boolean {
        return this.selectedPlan?.doc.customizable ?? false;
    }

    /**
     * Copies the UID of the onboarding organization to the clipboard.
     *
     * This method checks if the `onboardingInfo` object and its `uid` property are defined. If so,
     * it attempts to copy the `uid` to the user's clipboard using the Clipboard API.
     * It also handles success and error scenarios, logging appropriate messages to the console.
     */
    copyUid(): void {
        // Ensure `onboardingInfo` and `uid` are defined before attempting to copy
        if (this.onboardingInfo?.uid) {
            // Use the Clipboard API to write the UID to the clipboard
            navigator.clipboard.writeText(this.onboardingInfo.uid).catch((err) => {
                // If the copy operation fails (e.g., due to permission issues)

                console.error('Error copying UID: ', err); // Log error message
            });
        }
    }

    /**
     * Converts a Date object to a neutral date string in ISO format.
     * This method removes timezone information by creating a UTC date with only year, month, and day,
     * then converts it to ISO string format (YYYY-MM-DDTHH:mm:ss.sssZ).
     *
     * @param date - The Date object to convert
     * @returns A neutral date string in ISO format without timezone bias
     */
    toNeutralDate(date: Date): string {
        return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString();
    }

    toggleModule(moduleName: 'expert' | 'solutions' | 'csrd' | 'reduce' | 'mcpAccess'): void {
        const control = this.licenseFormGroup.get(moduleName);
        if (control) {
            control.setValue(!control.value);
            this.licenseFormGroup.markAsDirty();
        }
    }
}
